import {
    useCallback,
    useMemo,
    useState,
} from 'react'
import merge from 'lodash/merge'
import {
    GridGetRowsError,
    GridGetRowsParams,
    GridUpdateRowError,
} from '@mui/x-data-grid-pro'

import {
    useJWTToken,
} from 'app/Auth'
import simpleApi from 'app/services/simpleApi'
import {
    RequestConfig,
} from 'app/types/request.types'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import queryClient, {
    invalidateQueries,
} from 'app/queryClient'
import {
    SkyBaseDataSourceProvider,
    getFiltersFromFilterModel,
    useStableValue,
} from 'app/shared-components/SkyBaseDataGrid'

/* DataSource with fetchQuery */

type RequestParams = {
    sortField?: string,
    sortType?: 'desc' | 'asc',
    searchAll?: string,
    paginationModeMatches?: boolean,
    rows?: number,
    start?: number,
    archived?: boolean
    includeFilters: Record<string, any>,
}

type DataSourceParams = {
    request: RequestConfig,
    key: RequestKeys,
    rowsPerPage?: number,
}

export default function useDataGridSource({
    request,
    key,
    rowsPerPage = 25,
}: DataSourceParams): SkyBaseDataSourceProvider {
    const stableRequest = useStableValue<RequestConfig>(request)

    const token = useJWTToken()
    const [
        error,
        setError,
    ] = useState<string>(null)

    const handleError = useCallback((rowsError: GridGetRowsError | GridUpdateRowError) => {
        setError(rowsError.message)
    }, [])

    const customDataSource = useMemo(() => {
        return {
            getRows: async (params: GridGetRowsParams) => {
                const payload: RequestParams = {
                    sortField: params.sortModel[0]?.field,
                    sortType: params.sortModel[0]?.sort,
                    paginationModeMatches: rowsPerPage > 0,
                    rows: params.paginationModel.pageSize || rowsPerPage,
                    start: params.paginationModel.pageSize * params.paginationModel.page,
                    ...getFiltersFromFilterModel(params.filterModel),
                }

                const queryKey = [
                    key,
                    stableRequest.url,
                    payload,
                ]

                let result = {
                    rows: [],
                    rowCount: 0,
                }

                try {
                    const response = await queryClient.fetchQuery({
                        queryKey,
                        queryFn: () => {
                            return simpleApi({
                                ...stableRequest,
                                data: merge(payload, stableRequest.data),
                                token,
                            })
                        },
                        staleTime: 10000, // important to prevent irrelevant calls,
                    })

                    result = {
                        rows: response.items,
                        rowCount: response.matches || response.items.length,
                    }
                } catch (e) {
                    handleError({
                        message: e,
                    } as GridGetRowsError)
                }
                return result
            },
        }
    }, [
        rowsPerPage,
        key,
        stableRequest,
        token,
        handleError,
    ])

    const invalidate = useCallback(() => {
        invalidateQueries([key])
    }, [key])

    return useMemo(() => {
        return {
            dataSource: customDataSource,
            onDataSourceError: handleError,
            errorMessage: error,
            key,
            invalidate,
            request: stableRequest,
        }
    }, [
        customDataSource,
        handleError,
        error,
        key,
        invalidate,
        stableRequest,
    ])
}
